/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo } from "react";
import React, { useState, useEffect } from "react";
import axios from 'axios';
import { API_BASE_URL } from '../services/config';

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Cookies werden immer mit gesendet (httpOnly-Cookie-Auth)
axios.defaults.withCredentials = true;

// The Soft UI Dashboard PRO React main context
const SoftUI = createContext(null);

// Setting custom name for the context which is visible on react dev tools
SoftUI.displayName = "SoftUIContext";

// Soft UI Dashboard PRO React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Soft UI Dashboard PRO React context provider
function SoftUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
  };

  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <SoftUI.Provider value={value}>{children}</SoftUI.Provider>;
}

// Typechecking props for the SoftUIControllerProvider
SoftUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Soft UI Dashboard PRO React custom hook for using context
function useSoftUIController() {
  const context = useContext(SoftUI);

  if (!context) {
    throw new Error("useSoftUIController should be used inside the SoftUIControllerProvider.");
  }

  return context;
}

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });

const AuthContext = createContext();
const RESTRICTED_PORTAL_MODE = "sboffice_iaas_restricted";
const SCHLOERMANN_PORTAL_HOST = "portal.schloermann.systems";
const SCHLOERMANN_SUPPORT_EMAIL = "support@schloermann.systems";
const SCHLOERMANN_SUPPORT_PHONE = "041317023200";
const RESTRICTED_PORTAL_FALLBACKS = {
  [SCHLOERMANN_PORTAL_HOST]: {
    brand_name: "SBO-Portal",
    logo_url: null,
    favicon_url: null,
    primary_color: "#8b0000",
    portal_domain: SCHLOERMANN_PORTAL_HOST,
    portal_mode: RESTRICTED_PORTAL_MODE,
    support_email: SCHLOERMANN_SUPPORT_EMAIL,
    support_phone: SCHLOERMANN_SUPPORT_PHONE,
    portal_home_title: "Willkommen im SBO-Portal",
    portal_home_intro:
      "Ihr Kundenportal für SBOffice, IaaS-Services, Dokumentation und Support.",
    portal_home_sections: null,
  },
};

// Zentraler Axios-Interceptor – nur 401-Handling, kein manuelles Token-Management
const setupAxiosInterceptors = (setIsAuthenticated, setUser) => {
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error?.response?.status === 401) {
        setIsAuthenticated(false);
        setUser(null);
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
};

const getBrowserHostname = () => {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return "";
  }
  return window.location.hostname.toLowerCase();
};

const getRestrictedPortalFallback = (hostname) => {
  return RESTRICTED_PORTAL_FALLBACKS[hostname] || null;
};

const applyDomainPortalOverrides = (settings, hostname) => {
  if (!settings || hostname !== SCHLOERMANN_PORTAL_HOST) {
    return settings;
  }

  return {
    ...settings,
    portal_domain: SCHLOERMANN_PORTAL_HOST,
    portal_mode: RESTRICTED_PORTAL_MODE,
    support_email: SCHLOERMANN_SUPPORT_EMAIL,
    support_phone: SCHLOERMANN_SUPPORT_PHONE,
  };
};

const mergeActivePortalSettings = (publicSettings, authenticatedSettings) => {
  if (!publicSettings) {
    return authenticatedSettings;
  }

  if (authenticatedSettings?.portal_domain === publicSettings.portal_domain) {
    return {
      ...publicSettings,
      brand_name: authenticatedSettings.brand_name ?? publicSettings.brand_name,
      logo_url: authenticatedSettings.logo_url ?? publicSettings.logo_url,
      favicon_url: authenticatedSettings.favicon_url ?? publicSettings.favicon_url,
      primary_color: authenticatedSettings.primary_color ?? publicSettings.primary_color,
      portal_mode: authenticatedSettings.portal_mode ?? publicSettings.portal_mode,
      support_email: authenticatedSettings.support_email ?? publicSettings.support_email,
      support_phone: authenticatedSettings.support_phone ?? publicSettings.support_phone,
      portal_home_title: authenticatedSettings.portal_home_title ?? publicSettings.portal_home_title,
      portal_home_intro: authenticatedSettings.portal_home_intro ?? publicSettings.portal_home_intro,
      portal_home_sections: authenticatedSettings.portal_home_sections ?? publicSettings.portal_home_sections,
    };
  }

  return publicSettings;
};

// Auth Provider component
const AuthProvider = ({ children }) => {
  const [browserHostname] = useState(getBrowserHostname);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [partnerSettings, setPartnerSettings] = useState(null);
  const [publicPortalSettings, setPublicPortalSettings] = useState(null);
  const [portalSettingsLoading, setPortalSettingsLoading] = useState(true);
  const [customerPrices, setCustomerPrices] = useState({});

  // Partner-Settings + individuelle Kundenpreise laden
  const loadPartnerSettings = async (currentUser) => {
    if (!currentUser) return;
    try {
      if (currentUser.isPartner) {
        // Partner selbst – eigene Settings laden
        const res = await axios.get(`${API_BASE_URL}/api/partner-settings/my`);
        setPartnerSettings(res.data);
        setCustomerPrices({});
      } else if (currentUser.partnerId) {
        // Kunde eines Partners – Settings des Partners + eigene Kundenpreise laden
        const [settingsRes, pricesRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/partner-settings/for-partner/${currentUser.partnerId}`),
          axios.get(`${API_BASE_URL}/api/customer-prices/my`)
        ]);
        setPartnerSettings(settingsRes.data);
        setCustomerPrices(pricesRes.data || {});
      } else {
        setPartnerSettings(null);
        setCustomerPrices({});
      }
    } catch {
      setPartnerSettings(null);
      setCustomerPrices({});
    }
  };

  // Initialisiere Axios-Interceptors
  useEffect(() => {
    setupAxiosInterceptors(setIsAuthenticated, setUser);
  }, []);

  useEffect(() => {
    const loadPublicPortalSettings = async () => {
      const hostname = getBrowserHostname();
      const hostnameToUse = browserHostname || hostname;
      if (!hostnameToUse) {
        setPortalSettingsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/partner-settings/public/by-domain/${encodeURIComponent(hostnameToUse)}`,
          { timeout: 5000 }
        );
        setPublicPortalSettings(response.data || null);
      } catch {
        setPublicPortalSettings(null);
      } finally {
        setPortalSettingsLoading(false);
      }
    };

    loadPublicPortalSettings();
  }, [browserHostname]);

  // Beim Laden prüfen ob eine gültige Session (Cookie) existiert
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/webusers/verify`, { withCredentials: true });

        if (response.data?.valid) {
          setUser(response.data.user);
          setIsAuthenticated(true);
          await loadPartnerSettings(response.data.user);
        } else {
          setIsAuthenticated(false);
          setUser(null);
          setPartnerSettings(null);
        }
      } catch {
        setIsAuthenticated(false);
        setUser(null);
        setPartnerSettings(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Wird nach erfolgreichem Login aufgerufen – Cookie wurde bereits server-seitig gesetzt
  const login = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/webusers/verify`, { withCredentials: true });

      if (response.data?.valid) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        await loadPartnerSettings(response.data.user);
      } else {
        throw new Error('Token-Validierung fehlgeschlagen');
      }
    } catch (error) {
      setIsAuthenticated(false);
      setUser(null);
      setPartnerSettings(null);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/webusers/logout`, undefined, { withCredentials: true });
    } catch {
      // Auch bei Fehler lokal ausloggen
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setPartnerSettings(null);
      setCustomerPrices({});
      window.location.href = '/login';
    }
  };

  const activePortalSettings = useMemo(() => {
    const domainPortalSettings =
      publicPortalSettings || getRestrictedPortalFallback(browserHostname);
    return applyDomainPortalOverrides(
      mergeActivePortalSettings(domainPortalSettings, partnerSettings),
      browserHostname
    );
  }, [browserHostname, publicPortalSettings, partnerSettings]);
  const isRestrictedPortal = activePortalSettings?.portal_mode === RESTRICTED_PORTAL_MODE;

  const value = {
    isAuthenticated,
    isLoading,
    user,
    partnerSettings,
    setPartnerSettings,
    publicPortalSettings,
    portalSettingsLoading,
    activePortalSettings,
    isRestrictedPortal,
    customerPrices,
    setCustomerPrices,
    login,
    logout
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Typechecking props for the AuthProvider
AuthProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export {
  SoftUIControllerProvider,
  useSoftUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  AuthContext,
  AuthProvider,
};

